import React, { useState } from "react";
import {
  Briefcase,
  FileText,
  Gauge,
  Target,
  Wrench,
  Film,
  PenLine,
  Users,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import { usePrice } from "@/lib/price";

const tools = [
  {
    id: "playbook",
    name: "Playbook Directory",
    badge: "FREE",
    caption: "Hundreds of grounded income & job playbooks.",
    desc: "Every guide carries a 'Grounded' score — source-reported pay, costs and risk. Premium Action Guides ship as long-form PDFs with start-fast steps, real sources and honest numbers.",
    img: "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/playbooks.png",
    icon: Target,
    cta: "Browse playbooks",
    href: "/playbooks",
    color: "bg-[#FFE600]",
  },
  {
    id: "jobs",
    name: "Job Directory",
    badge: "FREE",
    caption: "338,947+ live jobs from 18,000+ company boards.",
    desc: "Search live roles direct from employer boards — no middleman. Filter by level, type, remote, 14 categories (Engineering, Healthcare, Sales, Nursing, Marketing…). Save searches, set Job Alerts, track applications.",
    img: "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/jobs.png",
    icon: Briefcase,
    cta: "Open the job directory",
    href: "/jobs",
    color: "bg-[#0055FF]",
    fg: "text-white",
  },
  {
    id: "resume",
    name: "Resume Builder",
    badge: "ALL-ACCESS",
    caption: "A role-ready resume in minutes.",
    desc: "Two ways in: upload your PDF/Word and we'll sharpen it, or generate from scratch. Pick from 4 styles (Classic · Modern · Elegant · Bold), tailor to any job post, watch the preview restyle live, and download. ATS-tuned, hyperlink certification matching included.",
    img: "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/resume.png",
    icon: FileText,
    cta: "Build your resume",
    href: "/resume",
    color: "bg-[#FF3B30]",
    fg: "text-white",
  },
  {
    id: "toolbox",
    name: "The Toolbox",
    badge: "FREE",
    caption: "252 entries · 86 categories · tools, prompts, repos, GPTs.",
    desc: "Every tool, prompt and repo from every video — one searchable directory. 192 Tools · 35 Prompts · 23 Repos · 2 GPTs, each linked back to the playbook that uses it.",
    img: "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/toolbox.png",
    icon: Wrench,
    cta: "Open the toolbox",
    href: "/toolbox",
    color: "bg-[#00FF66]",
  },
  {
    id: "simulator",
    name: "Readiness Simulator",
    badge: "ALL-ACCESS",
    caption: "Score & sharpen your interview with teach-back grading.",
    desc: "Voice + text role-play with brutal, scored feedback. Teach-back grading until you stop fumbling. Walk in calm.",
    img: "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/simulator.png",
    icon: Gauge,
    cta: "Run the simulator",
    href: "/simulator",
    color: "bg-[#FFE600]",
  },
  {
    id: "omnicut",
    name: "OmniCut",
    badge: "ALL-ACCESS",
    caption: "Frame-by-frame viral edit blueprints.",
    desc: "Custom ChatGPT that breaks down what makes a short pop and rebuilds it for your story. The content engine on top of the career engine.",
    img: "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/omnicut.png",
    icon: Film,
    cta: "Open OmniCut",
    href: "/omnicut",
    color: "bg-[#FF00FF]",
    fg: "text-white",
  },
  {
    id: "omniscript",
    name: "OmniScript",
    badge: "SOON",
    caption: "AI scripts for your videos.",
    desc: "The script engine that powers the OmniCut edit. Hooks, beats, payoff. Ship the script — then ship the cut.",
    img: "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/omniscript.png",
    icon: PenLine,
    cta: "Join the waitlist",
    href: "/omniscript",
    color: "bg-[#0055FF]",
    fg: "text-white",
  },
  {
    id: "community",
    name: "Community",
    badge: "INCLUDED",
    caption: "A community that actually ships.",
    desc: "Members already running the system inside Whop. Skool launches soon. Real receipts, real questions, zero gurus.",
    img: "https://storage.googleapis.com/jobhacki-assets/site/demo-tools/v2/community.png",
    icon: Users,
    cta: "See the community",
    href: "#comm",
    color: "bg-[#FDFBF7]",
  },
];

const badgeStyle = {
  FREE: "bg-[#00FF66] text-[#0a0a0a]",
  "ALL-ACCESS": "bg-[#FF3B30] text-white",
  SOON: "bg-[#0a0a0a] text-[#FFE600]",
  INCLUDED: "bg-[#0055FF] text-white",
};

export default function ToolBreakdown() {
  const [active, setActive] = useState(0);
  const { price } = usePrice();
  const cur = tools[active];

  return (
    <section
      id="tb"
      data-testid="tool-breakdown"
      className="relative border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="mb-12 md:mb-14 reveal">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            ★ THE VAULT
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl">
            Every tool,
            <br />
            <span className="bg-[#FFE600] px-2">broken down.</span>
          </h2>
          <p className="font-mono-body mt-5 max-w-2xl text-[#0a0a0a]/75 text-base md:text-lg">
            Click a tool to see exactly what it does. The free three unlock
            with an account; the rest unlock with a {price} one-time payment.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Left: tool rows */}
          <div className="lg:col-span-5 flex flex-col gap-3">
            {tools.map((t, i) => {
              const isActive = i === active;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  data-testid={`tb-row-${t.id}`}
                  onClick={() => setActive(i)}
                  className={`text-left transition-all w-full border-4 border-[#0a0a0a] ${
                    isActive
                      ? `${t.color} ${t.fg || "text-[#0a0a0a]"} shadow-brutal -translate-x-1 -translate-y-1`
                      : "bg-white hover:bg-[#FDFBF7] shadow-brutal-sm hover-pop"
                  } p-4 md:p-5`}
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] flex items-center justify-center">
                      <Icon size={22} strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-[19px] md:text-[22px]">
                          {t.name}
                        </span>
                        <span
                          className={`font-accent text-[10px] px-2 py-0.5 border-2 border-[#0a0a0a] ${badgeStyle[t.badge]}`}
                        >
                          {t.badge}
                        </span>
                      </div>
                      <p className="font-mono-body text-[13px] mt-1.5 opacity-80 leading-snug">
                        {t.caption}
                      </p>
                      {isActive && (
                        <>
                          <p className="font-mono-body text-[13px] mt-3 leading-relaxed">
                            {t.desc}
                          </p>
                          <a
                            href={t.href}
                            data-testid={`tb-cta-${t.id}`}
                            className="inline-flex items-center gap-1.5 mt-3 font-accent text-sm underline underline-offset-4 decoration-2 hover:no-underline"
                          >
                            {t.cta}
                            <ArrowUpRight size={14} strokeWidth={3} />
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right: live screenshot */}
          <div className="lg:col-span-7 lg:sticky lg:top-24 self-start">
            <div className="relative border-4 border-[#0a0a0a] bg-white shadow-brutal-lg overflow-hidden">
              <span
                className={`absolute top-4 right-4 z-10 font-accent text-xs px-2.5 py-1 border-2 border-[#0a0a0a] ${badgeStyle[cur.badge]}`}
              >
                {cur.badge}
              </span>
              <img
                key={cur.id}
                data-testid="tb-active-image"
                src={cur.img}
                alt={cur.name}
                className="w-full h-auto object-cover transition-opacity"
              />
            </div>
            <div className="mt-4 flex items-center justify-between px-1">
              <div>
                <div className="font-display text-2xl">{cur.name}</div>
                <div className="font-mono-body text-xs text-[#0a0a0a]/60 mt-1">
                  {cur.caption}
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 font-mono-body text-xs text-[#0a0a0a]/60">
                <CheckCircle2 size={14} className="text-[#0055FF]" />
                live preview
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
